import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import DashboardLayout from '@/components/DashboardLayout'
import GlassCard from '@/components/ui/GlassCard'
import GradientButton from '@/components/ui/GradientButton'
import {
  Users,
  MessageSquare,
  Building2,
  TrendingUp,
  AlertCircle,
  Clock,
  Eye,
  Plus,
  Download,
  ChevronLeft,
  ChevronRight,
  Calendar,
  CheckCircle,
  QrCode
} from 'lucide-react'
import api from '@/services/api'
import AgentQRModal from '@/components/AgentQRModal'



interface Analytics {
  totalLeads: number
  totalConversations: number
  totalProperties: number
  averageScore: number
}

interface Announcement {
  id: number
  title: string
  type: 'image' | 'video'
  file: string
  link: string | null
  active: boolean
  targetPlans: string[]
}

interface Alert {
  type: 'unattended' | 'upcoming' | 'expiring'
  count: number
  label: string
  icon: any
  color: string
}

interface RecentLead {
  id: string
  name: string
  temperature: 'hot' | 'warm' | 'cold'
  created_at: string
}

interface ActivityToday {
  id: string
  lead_id: string
  activity_type: string
  title: string
  scheduled_at: string
  status: string
  lead_name: string
  lead_phone: string
}

const ACTIVITY_ICONS: Record<string, string> = {
  call: '📞',
  whatsapp: '💬',
  email: '📧',
  visit: '🏠',
  meeting: '🤝',
  property_show: '🔑',
  follow_up: '📋',
  note: '📝'
}

const DashboardMain: React.FC = () => {
  const { t } = useTranslation('dashboard')
  const { agent, refreshAgent } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [analytics, setAnalytics] = useState<Analytics>({
    totalLeads: 0,
    totalConversations: 0,
    totalProperties: 0,
    averageScore: 0
  })
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0)
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [recentLeads, setRecentLeads] = useState<RecentLead[]>([])
  const [activitiesToday, setActivitiesToday] = useState<ActivityToday[]>([])
  const [loading, setLoading] = useState(true)
  const [showAgentQRModal, setShowAgentQRModal] = useState(false)

  useEffect(() => {
    fetchData()
    syncWithStripe()
  }, [])

  const syncWithStripe = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return
      const res = await fetch('/api/billing/sync', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        if (data.synced) refreshAgent()
      }
    } catch (e) {
      // Silencioso — no afecta la carga del dashboard
    }
  }

  // Detectar upgrade exitoso y refrescar agente
  useEffect(() => {
    const upgradeStatus = searchParams.get('upgrade')
    if (upgradeStatus === 'success') {
      refreshAgent()
      searchParams.delete('upgrade')
      setSearchParams(searchParams)
    }
  }, [searchParams])

  // Detectar bienvenida post-registro o post-checkout
  const [showWelcomeModal, setShowWelcomeModal] = useState(false)
  const [welcomePaid, setWelcomePaid] = useState(false)
  const [welcomePlan, setWelcomePlan] = useState('')

  useEffect(() => {
    const welcome = searchParams.get('welcome')
    const paid    = searchParams.get('paid')
    const plan    = searchParams.get('plan')
    if (welcome === 'true') {
      setWelcomePaid(paid === 'true')
      setWelcomePlan(plan || '')
      setShowWelcomeModal(true)
      if (paid === 'true') refreshAgent()
      // Limpiar URL sin recargar
      searchParams.delete('welcome')
      searchParams.delete('paid')
      searchParams.delete('plan')
      setSearchParams(searchParams, { replace: true })
    }
  }, [])

  useEffect(() => {
    if (announcements.length > 1) {
      const interval = setInterval(() => {
        setCurrentAnnouncementIndex((prev) => (prev + 1) % announcements.length)
      }, 5000)
      return () => clearInterval(interval)
    }
  }, [announcements])

  const fetchData = async () => {
    try {
      const [analyticsRes, announcementsRes, leadsRes, activitiesRes] = await Promise.all([
        api.get('/api/agents/analytics'),
        api.get('/api/agents/announcements'),
        api.get('/api/agents/leads?limit=5'),
        api.get('/api/agents/activities/today')
      ])

      setAnalytics(analyticsRes.data)
      setAnnouncements(announcementsRes.data.announcements || [])
      setRecentLeads(leadsRes.data.leads || [])
      setActivitiesToday(activitiesRes.data.activities || [])

      // Calcular alertas
      const unattended = leadsRes.data.leads.filter((l: any) => l.status === 'new').length
      const activitiesCount = activitiesRes.data.count || 0

      setAlerts([
        {
          type: 'unattended',
          count: unattended,
          label: t('unattendedLeads'),
          icon: AlertCircle,
          color: 'text-red-400'
        },
        {
          type: 'upcoming',
          count: activitiesCount,
          label: t('todayActivitiesAlert'),
          icon: Calendar,
          color: 'text-yellow-400'
        }
      ])
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCompleteActivity = async (activityId: string) => {
    try {
      await api.put(`/api/agents/activities/${activityId}`, {
        status: 'completed',
        outcome: 'completed',
        outcome_notes: 'Completada desde dashboard'
      })
      fetchData()
    } catch (error) {
      console.error('Error completing activity:', error)
    }
  }

  const stats = [
    {
      title: t('totalLeads'),
      value: analytics.totalLeads,
      icon: Users,
      gradientFrom: 'from-blue-500',
      gradientTo: 'to-blue-600'
    },
    {
      title: t('conversations'),
      value: analytics.totalConversations,
      icon: MessageSquare,
      gradientFrom: 'from-green-500',
      gradientTo: 'to-green-600'
    },
    {
      title: t('properties'),
      value: analytics.totalProperties,
      icon: Building2,
      gradientFrom: 'from-purple-500',
      gradientTo: 'to-purple-600'
    },
    {
      title: t('averageScore'),
      value: `${Math.round(analytics.averageScore)}`,
      icon: TrendingUp,
      gradientFrom: 'from-orange-500',
      gradientTo: 'to-orange-600'
    }
  ]

  const getTemperatureIcon = (temp: string) => {
    switch (temp) {
      case 'hot': return '🔥'
      case 'warm': return '☀️'
      case 'cold': return '❄️'
      default: return '🌡️'
    }
  }

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 60) return t('timeAgoMin', { count: diffMins })
    if (diffHours < 24) return t('timeAgoHour', { count: diffHours })
    return t('timeAgoDay', { count: diffDays })
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Welcome Section */}
        <GlassCard className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 border-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">
                {t('welcomeBack', { name: agent?.name })}
              </h2>
              <p className="text-blue-100">
                {t('siteAvailableAt')}{' '}
                <a
                  href={`https://${agent?.subdomain}.innovagent.tech`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold underline hover:text-white transition-colors"
                >
                  {agent?.subdomain}.innovagent.tech
                </a>
              </p>
            </div>
            <button
              onClick={() => setShowAgentQRModal(true)}
              className="flex flex-col items-center justify-center p-4 bg-white/20 hover:bg-white/30 rounded-xl transition-all hover:scale-105 group"
              title={t('generateQR')}
            >
              <QrCode className="h-12 w-12 text-white mb-1" />
              <span className="text-xs text-white/80 group-hover:text-white">{t('myQR')}</span>
            </button>
          </div>
        </GlassCard>

        {/* Stats Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <GlassCard key={index} className="p-6 hover:scale-105 transition-transform">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-r ${stat.gradientFrom} ${stat.gradientTo}`}>
                      <stat.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <h3 className="text-gray-400 text-sm font-medium mb-1">{stat.title}</h3>
                  <p className="text-3xl font-bold text-white">{stat.value}</p>
                </GlassCard>
              ))}
            </div>

            {/* Announcements Carousel */}
            {announcements.length > 0 && (
              <GlassCard className="p-6">
                <div className="relative">
                  {announcements[currentAnnouncementIndex].type === 'image' ? (
                    <img
                      src={`/announcements/${announcements[currentAnnouncementIndex].file}`}
                      alt={announcements[currentAnnouncementIndex].title}
                      className="w-full h-48 object-cover rounded-xl cursor-pointer"
                      onClick={() => {
                        const link = announcements[currentAnnouncementIndex].link
                        if (link) {
                          if (link.startsWith('http')) {
                            window.open(link, '_blank')
                          } else {
                            navigate(link)
                          }
                        }
                      }}
                    />
                  ) : (
                    <video
                      src={`/announcements/${announcements[currentAnnouncementIndex].file}`}
                      className="w-full h-48 object-cover rounded-xl cursor-pointer"
                      autoPlay
                      loop
                      muted
                      onClick={() => {
                        const link = announcements[currentAnnouncementIndex].link
                        if (link) {
                          if (link.startsWith('http')) {
                            window.open(link, '_blank')
                          } else {
                            navigate(link)
                          }
                        }
                      }}
                    />
                  )}

                  {announcements.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentAnnouncementIndex((prev) => (prev - 1 + announcements.length) % announcements.length)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </button>
                      <button
                        onClick={() => setCurrentAnnouncementIndex((prev) => (prev + 1) % announcements.length)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                      >
                        <ChevronRight className="h-6 w-6" />
                      </button>

                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                        {announcements.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentAnnouncementIndex(idx)}
                            className={`h-2 rounded-full transition-all ${
                              idx === currentAnnouncementIndex ? 'w-8 bg-white' : 'w-2 bg-white/50'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </GlassCard>
            )}

            {/* Important Alerts */}
            <GlassCard className="p-6">
              <h3 className="text-xl font-bold text-white mb-4">{t('importantAlerts')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {alerts.map((alert, idx) => (
                  <div
                    key={idx}
                    className="flex items-center space-x-4 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
                    onClick={() => navigate(alert.type === 'upcoming' ? '/dashboard/leads?tab=seguimiento' : '/dashboard/leads')}
                  >
                    <alert.icon className={`h-8 w-8 ${alert.color}`} />
                    <div className="flex-1">
                      <p className="text-white font-medium">{alert.label}</p>
                      <p className={`text-2xl font-bold ${alert.color}`}>{alert.count}</p>
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>

            {/* Actividades de Hoy */}
            <GlassCard className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white flex items-center space-x-2">
                  <Calendar className="h-6 w-6 text-yellow-400" />
                  <span>{t('todayActivities')}</span>
                </h3>
                <button
                  onClick={() => navigate('/dashboard/leads?tab=seguimiento')}
                  className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                >
                  {t('viewAll')}
                </button>
              </div>
              {activitiesToday.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">{t('noTodayActivities')}</p>
                  <button
                    onClick={() => navigate('/dashboard/leads?tab=seguimiento')}
                    className="mt-3 text-blue-400 hover:text-blue-300 text-sm font-medium"
                  >
                    {t('scheduleActivity')}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {activitiesToday.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{ACTIVITY_ICONS[activity.activity_type] || '📌'}</span>
                        <div>
                          <p className="text-white font-medium">{activity.title}</p>
                          <p className="text-gray-400 text-sm">
                            {formatTime(activity.scheduled_at)} • {activity.lead_name || t('noLead')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleCompleteActivity(activity.id)}
                          className="p-2 hover:bg-green-500/20 rounded-lg transition-colors"
                          title={t('markCompleted')}
                        >
                          <CheckCircle className="h-5 w-5 text-green-400" />
                        </button>
                        <button
                          onClick={() => navigate('/dashboard/leads?tab=seguimiento')}
                          className="p-2 hover:bg-blue-500/20 rounded-lg transition-colors"
                          title={t('viewDetail')}
                        >
                          <Eye className="h-5 w-5 text-blue-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>

            {/* Recent Activity */}
            <GlassCard className="p-6">
              <h3 className="text-xl font-bold text-white mb-4">{t('recentLeads')}</h3>
              {recentLeads.length === 0 ? (
                <p className="text-gray-400 text-center py-8">{t('noRecentActivity')}</p>
              ) : (
                <div className="space-y-3">
                  {recentLeads.slice(0, 5).map((lead) => (
                    <div
                      key={lead.id}
                      className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
                      onClick={() => navigate('/dashboard/leads')}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{getTemperatureIcon(lead.temperature)}</span>
                        <div>
                          <p className="text-white font-medium">{lead.name || t('noName')}</p>
                          <p className="text-gray-400 text-sm">{getTimeAgo(lead.created_at)}</p>
                        </div>
                      </div>
                      <Eye className="h-5 w-5 text-gray-400" />
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>

            {/* Quick Actions */}
            <GlassCard className="p-6">
              <h3 className="text-xl font-bold text-white mb-4">{t('quickActions')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <GradientButton
                  onClick={() => navigate('/dashboard/leads?temperature=hot')}
                  className="flex flex-col items-center justify-center p-6 h-auto"
                >
                  <Users className="h-8 w-8 mb-2" />
                  <span className="font-medium">{t('viewHotLeads')}</span>
                </GradientButton>

                <GradientButton
                  onClick={() => navigate('/dashboard/properties')}
                  className="flex flex-col items-center justify-center p-6 h-auto"
                >
                  <Plus className="h-8 w-8 mb-2" />
                  <span className="font-medium">{t('addProperty')}</span>
                </GradientButton>

                <GradientButton
                  onClick={() => navigate('/dashboard/analytics')}
                  className="flex flex-col items-center justify-center p-6 h-auto"
                >
                  <Download className="h-8 w-8 mb-2" />
                  <span className="font-medium">{t('viewAnalytics')}</span>
                </GradientButton>

                <GradientButton
                  onClick={() => navigate('/dashboard/leads?tab=seguimiento')}
                  className="flex flex-col items-center justify-center p-6 h-auto"
                >
                  <Calendar className="h-8 w-8 mb-2" />
                  <span className="font-medium">{t('followUp')}</span>
                </GradientButton>
              </div>
            </GlassCard>
          </>
        )}
      </div>

      {/* Modal de Bienvenida post-registro / post-checkout */}
      {showWelcomeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/10 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center">
              <div className="text-5xl mb-4">{welcomePaid ? '🎉' : '👋'}</div>
              <h2 className="text-2xl font-bold text-white mb-3">
                {welcomePaid ? t('welcomeTitlePaid') : t('welcomeTitleFree')}
              </h2>
              {welcomePaid ? (
                <p className="text-white/70 mb-6">
                  {t('welcomeBodyPaid')}
                </p>
              ) : (
                <p className="text-white/70 mb-6">
                  {t('welcomeBodyFreePre')}{' '}
                  <span className="text-blue-400 font-semibold">{t('welcomeBodyFreeTrial')}</span>{' '}
                  {t('welcomeBodyFreePost')}
                </p>
              )}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowWelcomeModal(false)
                    navigate('/dashboard/profile')
                  }}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-semibold rounded-xl transition-all"
                >
                  {t('completeProfile')}
                </button>
                <button
                  onClick={() => setShowWelcomeModal(false)}
                  className="w-full py-3 bg-white/10 hover:bg-white/20 text-white/70 font-medium rounded-xl transition-all"
                >
                  {t('exploreDashboard')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal QR del Agente */}
      <AgentQRModal
        isOpen={showAgentQRModal}
        onClose={() => setShowAgentQRModal(false)}
      />
    </DashboardLayout>
  )
}

export default DashboardMain
